import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import Ribbon, { RibbonItem } from "@components/Ribbon/Ribbon";
import LazyImage from "@components/LazyImage/LazyImage";
import placeholderImg from "../../../../img/image-placeholder.png";
import { sanitizeUrl } from "@helpers";
import { components } from "@apiSchema";
import cn from "classnames";
import {
    BACKGROUND_TRANSITION_DELAY,
    RIBBON_DEFAULTS
} from "@pages/Home/sections/HomeReportsAndNews/HomeReportsAndNews.helpers";

const HomeReports: FCX<{
    data: components["schemas"]["ReportProPublicationClient"][];
    setBgSrc: (src: string) => void;
    onNavReady?: (handlers: { next: () => void; prev: () => void }) => void;
}> = ({
    data: rawData,
    setBgSrc,
    onNavReady,
}) => {
    const {
        userInfo: {
            reportProDetailsUrl,
            products,
        },
    } = usePlatformContext();
    const [activeItemIndex, setActiveItemIndex] = useState<number | null>(0);
    const [backgroundIndex, setBackgroundIndex] = useState<number | null>(0);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setBackgroundIndex(activeItemIndex);
        }, BACKGROUND_TRANSITION_DELAY);

        return () => clearTimeout(timer);
    }, [activeItemIndex]);

    const defaultImageUrl = useMemo(
        () => backgroundIndex !== null ? rawData?.[backgroundIndex]?.imageUrl ?? '' : '',
        [backgroundIndex, rawData]
    );

    useEffect(() => {
        setBgSrc(defaultImageUrl);
    }, [defaultImageUrl, setBgSrc]);

    // Preload all report card images upfront
    useEffect(() => {
        rawData?.forEach(item => {
            if (item.imageUrl) {
                const img = new Image();
                img.src = item.imageUrl;
            }
        });
    }, [rawData]);

    const handleCardHover = useCallback((imageUrl: string) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setBgSrc(imageUrl || defaultImageUrl);
        }, 100);
    }, [setBgSrc, defaultImageUrl]);

    const handleCardLeave = useCallback(() => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setBgSrc(defaultImageUrl);
        }, 100);
    }, [setBgSrc, defaultImageUrl]);

    const reportProProduct = useMemo(
        () => products[AppId.ReportPro],
        [products]
    );

    const data = useMemo(
        (): RibbonItem[] => (rawData ?? []).map(i => ({
            id: i.id ?? 0,
            title: i.title ?? '',
            description: i.description ?? '',
            url: sanitizeUrl(`${reportProProduct.url}/${reportProDetailsUrl}/${i.id ?? ''}`),
            imageUrl: i.imageUrl ?? '',
        })) ?? [],
        [rawData, reportProDetailsUrl, reportProProduct]
    );

    const renderItem = useCallback((item: RibbonItem, index: number, isActive: boolean) => {
        return (
            <a
                href={item.url ?? ""}
                target="_blank"
                rel="noreferrer"
                key={item.id}
                className={cn("Ribbon__item", { "is-active": isActive })}
                onMouseEnter={() => handleCardHover(item.imageUrl ?? '')}
                onMouseLeave={handleCardLeave}
            >
                <LazyImage
                    src={item.imageUrl ?? ''}
                    alt={item.title}
                    className="Ribbon__item-bg-image"
                    placeholder={<img src={placeholderImg as string} alt="" />}
                />
                <div className="Ribbon__item-image-sizer">
                    <img src={item.imageUrl} alt={item.title} />
                </div>
                <div className="Ribbon__item-content">
                    <div className="Ribbon__item-fallback">
                        <div className="Ribbon__item-fallback-title">{item.title}</div>
                        <div className="Ribbon__item-fallback-description">{item.description}</div>
                    </div>
                </div>
            </a>
        );
    }, [handleCardHover, handleCardLeave]);

    return (
        <Ribbon
            {...RIBBON_DEFAULTS}
            maxSlidesPerView={8}
            minTileWidth={160}
            items={data}
            setActiveTileIndex={setActiveItemIndex}
            isAutoSlideDisabled
            className="appears-left"
            hideBuiltInNav
            onNavReady={onNavReady}
            renderItem={renderItem}
        />
    );
}

export default HomeReports;
